import type {PayloadStrategy} from '@/pages/components/audit/adjustment/adapters/types';
import {basicStrategy} from '@/pages/components/audit/adjustment/adapters/strategies/common-str';
import {
    CommissionPaymentWorkingPaper,
    DonationWorkingPaper,
    PurchasesWorkingPaper,
    WithholdingTaxWorkingPaper,
} from '@/services/auditService/interface/expenses';

export const expenseStrategy: PayloadStrategy = {
    buildPayload: ({prev, ctx, amount}) => basicStrategy.buildPayload({prev, ctx, amount}),
    readAmount: (payload: any) => basicStrategy.readAmount(payload),
};

// Rental Expenses strategy (RX1): ensure payload conforms to RentalExpensesWorkingPaper.
// Field WP→WP: keep top-level adjustment (+ drawer split maps) like loan field strategies.
export const rentalExpensesStrategy: PayloadStrategy = {
    buildPayload: ({prev, ctx, amount}) => {
        const p = (prev || {}) as any; // Use any for backward compatibility with legacy fields
        const annualId = ctx?.annualId;
        
        // Helper function to add annualId to each row
        const addAnnualId = <T extends Record<string, any>>(rows: T[]): T[] => {
            if (!Array.isArray(rows)) return rows;
            return rows.map(row => ({
                ...row,
                annualId: annualId
            }));
        };
        
        const rows = addAnnualId(Array.isArray(p.rows) ? p.rows : []);
        const relatedItems = addAnnualId(Array.isArray(p.relatedItems) ? p.relatedItems : []);
        
        const next: any = {};
        if (rows.length > 0) next.rows = rows;
        if (relatedItems.length > 0) next.relatedItems = relatedItems;
        // Always include rentalOtherInfo
        next.rentalOtherInfo = p.rentalOtherInfo || {
            monthsForPeriod: p.monthsForPeriod ?? null,
            extractPL: p.extractPL ?? '0',
            deletedMonthsByCol: p.deletedMonthsByCol && typeof p.deletedMonthsByCol === 'object' ? p.deletedMonthsByCol : {},
            rentalTotalsByCol: p.rentalTotalsByCol && typeof p.rentalTotalsByCol === 'object' ? p.rentalTotalsByCol : {},
        };

        // Field WP→WP signed amount (sibling of rows) — required for attr + AdjustTabs non-DTD entry
        const adj = Number(p.adjustment ?? amount);
        if (Number.isFinite(adj)) next.adjustment = adj;
        if (Number.isFinite(Number(p.rentalAdjustment))) {
            next.rentalAdjustment = Number(p.rentalAdjustment);
        }
        if (Number.isFinite(Number(p.otherExpenseAdjustment))) {
            next.otherExpenseAdjustment = Number(p.otherExpenseAdjustment);
        }
        if (p.rentalMonthAdjustments && typeof p.rentalMonthAdjustments === 'object') {
            next.rentalMonthAdjustments = p.rentalMonthAdjustments;
        }
        if (p.otherExpenseAdjustmentsById && typeof p.otherExpenseAdjustmentsById === 'object') {
            next.otherExpenseAdjustmentsById = p.otherExpenseAdjustmentsById;
        }
        if (Array.isArray(p.removeAdjustHeaderIds) && p.removeAdjustHeaderIds.length > 0) {
            next.removeAdjustHeaderIds = p.removeAdjustHeaderIds;
        }
        
        return next;
    },
    readAmount: (payload: any) => {
        const n = Number((payload || {}).adjustment);
        return Number.isFinite(n) ? n : (undefined as any);
    },
};

// DONATION strategy (RX3): ensure payload conforms to DonationWorkingPaper.
// Amount is handled via adjustmentAmount in the entry; payload holds structured working paper details.
export const donationStrategy: PayloadStrategy = {
    buildPayload: ({prev, ctx}) => {
        const p = (prev || {}) as Partial<DonationWorkingPaper>;
        const annualId = ctx?.annualId;
        
        // Helper function to add annualId to each row
        const addAnnualId = <T extends Record<string, any>>(rows: T[]): T[] => {
            if (!Array.isArray(rows)) return rows;
            return rows.map(row => ({
                ...row,
                annualId: annualId
            }));
        };
        
        const taxDeductible = addAnnualId(Array.isArray(p.taxDeductible) ? p.taxDeductible : []);
        const nonTaxDeductible = addAnnualId(Array.isArray(p.nonTaxDeductible) ? p.nonTaxDeductible : []);
        const relatedItems = addAnnualId(Array.isArray(p.relatedItems) ? p.relatedItems : []);
        
        const next: any = {};
        if (taxDeductible.length > 0) next.taxDeductible = taxDeductible;
        if (nonTaxDeductible.length > 0) next.nonTaxDeductible = nonTaxDeductible;
        if (relatedItems.length > 0) next.relatedItems = relatedItems;
        
        return next;
    },
    // Return undefined to let registry fall back to entry.adjustmentAmount
    readAmount: (payload: any) => {
        const n = Number((payload || {}).adjustment);
        return Number.isFinite(n) ? n : (undefined as any);
    },
};

// Commission Payment strategy (RX4): ensure payload conforms to CommissionPaymentWorkingPaper.
// Amount is handled via adjustmentAmount in the entry; payload holds structured working paper details.
export const commissionPaymentStrategy: PayloadStrategy = {
    buildPayload: ({prev, ctx}) => {
        const p = (prev || {}) as Partial<CommissionPaymentWorkingPaper>;
        const annualId = ctx?.annualId;
        
        // Helper function to add annualId to each row
        const addAnnualId = <T extends Record<string, any>>(rows: T[]): T[] => {
            if (!Array.isArray(rows)) return rows;
            return rows.map(row => ({
                ...row,
                annualId: annualId
            }));
        };
        
        const commissionPayments = addAnnualId(Array.isArray(p.commissionPayments) ? p.commissionPayments : []);
        const relatedItems = addAnnualId(Array.isArray(p.relatedItems) ? p.relatedItems : []);
        
        const next: any = {};
        if (commissionPayments.length > 0) next.commissionPayments = commissionPayments;
        if (relatedItems.length > 0) next.relatedItems = relatedItems;
        
        return next;
    },
    // Return undefined to let registry fall back to entry.adjustmentAmount
    readAmount: (payload: any) => {
        const n = Number((payload || {}).adjustment);
        return Number.isFinite(n) ? n : (undefined as any);
    },
};

// Withholding Tax strategy (R14 / PL-AE-WHT)
export const withholdingTaxStrategy: PayloadStrategy = {
    buildPayload: ({prev, ctx}) => {
        const p = (prev || {}) as Partial<WithholdingTaxWorkingPaper>;
        const annualId = ctx?.annualId;
        const addAnnualId = <T extends Record<string, any>>(rows: T[]): T[] => {
            if (!Array.isArray(rows)) return rows;
            return rows.map(row => ({...row, annualId}));
        };
        const whtRows = addAnnualId(Array.isArray(p.whtRows) ? p.whtRows : []);
        const relatedItems = addAnnualId(Array.isArray(p.relatedItems) ? p.relatedItems : []);
        const next: any = {};
        if (whtRows.length > 0) next.whtRows = whtRows;
        if (relatedItems.length > 0) next.relatedItems = relatedItems;
        return next;
    },
    readAmount: (payload: any) => {
        const rows = Array.isArray(payload?.whtRows) ? payload.whtRows : [];
        if (rows.length === 0) {
            const n = Number((payload || {}).adjustment);
            return Number.isFinite(n) ? n : (undefined as any);
        }
        return rows.reduce((sum: number, row: any) => {
            const amt = Number(row?.withholdingTaxAmount ?? row?.perClient) || 0;
            const adj = Array.isArray(row?.adjustments)
                ? row.adjustments.reduce((s: number, d: any) => s + (Number(d?.adjustment) || 0), 0)
                : (Number(row?.adjustment) || 0);
            return sum + amt + adj;
        }, 0);
    },
};

// purchases (QE1): ensure payload conforms to ConsultancyFeeWorkingPaper.
// Amount is handled via adjustmentAmount in the entry; payload holds structured working paper details.
export const purchasesStrategy: PayloadStrategy = {
    buildPayload: ({prev, ctx}) => {
        const p = (prev || {}) as Partial<PurchasesWorkingPaper>;
        const annualId = ctx?.annualId;
        
        // Helper function to add annualId to each row
        const addAnnualId = <T extends Record<string, any>>(rows: T[]): T[] => {
            if (!Array.isArray(rows)) return rows;
            return rows.map(row => ({
                ...row,
                annualId: annualId
            }));
        };
        
        const purchases = addAnnualId(Array.isArray(p.purchases) ? p.purchases : []);
        const relatedItems = addAnnualId(Array.isArray(p.relatedItems) ? p.relatedItems : []);
        const transactionTest = addAnnualId(Array.isArray(p.transactionTest) ? p.transactionTest : []);
        const cutOffThisYearTest = addAnnualId(Array.isArray(p.cutOffThisYearTest) ? p.cutOffThisYearTest : []);
        const cutOffNextYearTest = addAnnualId(Array.isArray(p.cutOffNextYearTest) ? p.cutOffNextYearTest : []);
        
        const next: any = {};
        if (purchases.length > 0) next.purchases = purchases;
        if (relatedItems.length > 0) next.relatedItems = relatedItems;
        if (transactionTest.length > 0) next.transactionTest = transactionTest;
        if (cutOffThisYearTest.length > 0) next.cutOffThisYearTest = cutOffThisYearTest;
        if (cutOffNextYearTest.length > 0) next.cutOffNextYearTest = cutOffNextYearTest;
        
        return next;
    },
    // Return undefined to let registry fall back to entry.adjustmentAmount
    readAmount: (payload: any) => {
        const n = Number((payload || {}).adjustment);
        return Number.isFinite(n) ? n : (undefined as any);
    },
};

// Salaries Allowance strategy (QE2): ensure payload conforms to ISalariesAllowance.
export const salariesAllowanceStrategy: PayloadStrategy = {
    buildPayload: ({prev, ctx}) => {
        const p = (prev || {}) as any; // Use any for backward compatibility with legacy fields
        const annualId = ctx?.annualId;
        
        // Helper function to add annualId to each row
        const addAnnualId = <T extends Record<string, any>>(rows: T[]): T[] => {
            if (!Array.isArray(rows)) return rows;
            return rows.map(row => ({
                ...row,
                annualId: annualId
            }));
        };
        
        const staffRow = addAnnualId(Array.isArray(p.staffRow) ? p.staffRow : []);
        const workerRow = addAnnualId(Array.isArray(p.workerRow) ? p.workerRow : []);
        const directorRow = addAnnualId(Array.isArray(p.directorRow) ? p.directorRow : []);
        const directorQuarterRow = addAnnualId(Array.isArray(p.directorQuarterRow) ? p.directorQuarterRow : []);
        
        const next: any = {};
        if (staffRow.length > 0) next.staffRow = staffRow;
        if (workerRow.length > 0) next.workerRow = workerRow;
        if (directorRow.length > 0) next.directorRow = directorRow;
        if (directorQuarterRow.length > 0) next.directorQuarterRow = directorQuarterRow;
        
        return next;
    },
    // Return undefined to let registry fall back to entry.adjustmentAmount
    readAmount: (payload: any) => {
        const n = Number((payload || {}).adjustment);
        return Number.isFinite(n) ? n : (undefined as any);
    },
};
// Salaries & Wages strategy (PL-AE-SAL / PL-AE-WAG): new separated salary/wages WP.
export const salariesWageStrategy: PayloadStrategy = {
    buildPayload: ({prev, ctx}) => {
        const p = (prev || {}) as any;
        const annualId = ctx?.annualId;
        
        const addAnnualId = <T extends Record<string, any>>(rows: T[]): T[] => {
            if (!Array.isArray(rows)) return rows;
            return rows.map(row => ({ ...row, annualId }));
        };
        
        const salary = addAnnualId(Array.isArray(p.salary) ? p.salary : []);
        
        const next: any = {};
        if (salary.length > 0) next.salary = salary;
        
        return next;
    },
    readAmount: (payload: any) => {
        const n = Number((payload || {}).adjustment);
        return Number.isFinite(n) ? n : (undefined as any);
    },
};

// Director Salary strategy (PL-AE-DR / PL-AE-DQ): new separated director WP.
export const directorSalaryStrategy: PayloadStrategy = {
    buildPayload: ({prev, ctx}) => {
        const p = (prev || {}) as any;
        const annualId = ctx?.annualId;
        
        const addAnnualId = <T extends Record<string, any>>(rows: T[]): T[] => {
            if (!Array.isArray(rows)) return rows;
            return rows.map(row => ({ ...row, annualId }));
        };
        
        const directorSalary = addAnnualId(Array.isArray(p.directorSalary) ? p.directorSalary : []);
        const quarter = addAnnualId(Array.isArray(p.quarter) ? p.quarter : []);
        
        const next: any = {};
        if (directorSalary.length > 0) next.directorSalary = directorSalary;
        if (quarter.length > 0) next.quarter = quarter;
        
        return next;
    },
    readAmount: (payload: any) => {
        const n = Number((payload || {}).adjustment);
        return Number.isFinite(n) ? n : (undefined as any);
    },
};

// MPF Summary strategy (PL-AE-MPF): read-only summary, no buildPayload needed.
export const mpfSummaryStrategy: PayloadStrategy = {
    buildPayload: ({prev}) => prev || {},
    readAmount: (payload: any) => {
        const n = Number((payload || {}).adjustment);
        return Number.isFinite(n) ? n : (undefined as any);
    },
};

// Interest Summary strategy (PL-FC-LI): keep loan save details + field adjustment when WP→WP from Bank/Vehicle.
// interestSummary 展示态由 InterestSummaryWp 使用；adjustment 提交转换见 toPaper / buildSubmitHeader。
export const interestSummaryStrategy: PayloadStrategy = {
    buildPayload: ({prev, amount}) => {
        const p = (prev || {}) as Record<string, any>;
        const next: Record<string, any> = {...p};
        // Preserve loan arrays for field-level WP→WP (inAdj ownership = bankLoans | vehicleLoans)
        if (Array.isArray(p.bankLoans) && p.bankLoans.length > 0) {
            next.bankLoans = p.bankLoans;
        }
        if (Array.isArray(p.vehicleLoans) && p.vehicleLoans.length > 0) {
            next.vehicleLoans = p.vehicleLoans;
        }
        const adj = Number(p.adjustment ?? amount);
        if (Number.isFinite(adj)) next.adjustment = adj;
        return next;
    },
    readAmount: (payload: any) => {
        const n = Number((payload || {}).adjustment);
        return Number.isFinite(n) ? n : (undefined as any);
    },
};